import { SOURCEAPP_ECHANNEL, SOURCEAPP_GOTGAFFILIATE } from '@/constants';
import { PlanType } from '@/enums';
import { getAPIPath } from '@/helpers/APIHelper';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import { getLppsaPlanClass } from '@/helpers/LppsaPlanClassHelper';
import { store } from '@/store';
import {
  AddressDetail,
  BankAccountDetail,
  InsuredDetail,
} from '@/types/InfoState';
import { PersonalDetail } from '@/types/InfoState/PersonalDetail';
import { QuotationData } from '@/types/PlanSummary';
import {
  CreateProposalRequest,
  InsuredData,
  InforcePolicyRequest,
  ProposerData,
  RetrieveProposalRequest,
} from '@/types/Proposal';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const inforcePolicy = createAsyncThunk(
  'application/InforcePolicy',
  async (encodedOrderId: string) => {
    try {
      const request: InforcePolicyRequest = {
        encodedOrderId,
        sendEmail: true,
        sendSMS: true,
        countryCode: getCountryCode(),
      };
      const relativeUrl = 'InforcePolicy';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const retrieveHomeProposal = createAsyncThunk(
  'application/Home/RetrieveProposal',
  async (request: RetrieveProposalRequest) => {
    try {
      const relativeUrl = `${getAPIPath()}/RetrieveProposal`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      if (response.data.succeeded)
        response.data.data.encodedOrderId = request.encodedOrderId;
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const createHomeProposal = createAsyncThunk(
  'application/Home/CreateProposal',
  async ({
    language,
    productCode,
  }: {
    language: string;
    productCode: string;
  }) => {
    const rootState = store.getState();
    const planSummary = rootState.planSummary;
    const info = rootState.infoState;
    const agentState = rootState.affiliate;
    const isLppsa = !!rootState.uiState.isLppsa;

    try {
      const relativeUrl = `${getAPIPath()}/CreateProposal`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;

      const request: CreateProposalRequest = {
        countryCode: getCountryCode(),
        productCode,
        startDate: format(
          parseISO(planSummary.quotationDetail.startDate),
          'dd/MM/yyyy',
        ),
        proposer: getProposer(
          info.personalDetail,
          info.bankAccountDetail,
          info.mailingDetail,
        ),
        insured: getInsured(info.insuredDetails, info.propertyDetail),
        quotationNo: planSummary.planPrice?.quotationNo
          ? planSummary.planPrice?.quotationNo
          : '',
        buildingSumInsured: planSummary.quotationDetail.buildingSumInsured,
        contentSumInsured: planSummary.quotationDetail.contentSumInsured,
        home: getHomeData(
          planSummary.quotationDetail,
          info.propertyDetail,
          isLppsa,
        ),
        declarationList: planSummary.declarationList,
        addOn: getAddOn(planSummary.addOn),
        referral: '',
        language,
        marketingConsent: planSummary.marketingConsent,
        declaration: planSummary.declaration,
        isPartner: false,
        agentCode: agentState.agentCode,
        isStaff: agentState.isValidStaff ?? false,
        clientPfNumber: agentState.clientPfNumber ?? '',
        affiliate: {
          affiliateId: agentState.agentId,
        },
        checkPremium: planSummary.planPrice?.totalPremium ?? 0,
        sourceAppId:
          agentState.agentId != null
            ? SOURCEAPP_GOTGAFFILIATE
            : SOURCEAPP_ECHANNEL,
        isLppsa,
      };

      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

const getHomeData = (
  data: QuotationData,
  propertyData: AddressDetail,
  isLppsa: boolean,
) => {
  return {
    ownership: data.ownership,
    buildingType: data.buildingType ?? 0,
    constructionType: data.constructionType ?? 0,
    planType: isLppsa ? PlanType.ContentProtection : data.planType,
    planClass: isLppsa ? getLppsaPlanClass(data.planType) : undefined,
    noOfStorey: data.noOfStorey,
    address: {
      address1: propertyData.addressLine1,
      address2: propertyData.addressLine2,
      city: propertyData.city,
      postcode: propertyData.postcode,
      state: propertyData.state,
      country: propertyData.country,
    },
  };
};

export const getInsured = (
  data: InsuredDetail[],
  propertyData: AddressDetail,
): InsuredData[] | undefined => {
  if (!data.length) {
    return undefined;
  }

  return data.map((insured) => ({
    type: 1,
    salutation: '',
    name: insured.name,
    formerName: insured.formerName,
    aliasName: insured.aliasName,
    lastName: '',
    identityType: insured.idType,
    identityNumber: insured.idNumber.toString(),
    idNoExpiryDate: insured.idNoExpiryDate || undefined,
    secondaryIdentityType:
      insured.secondaryIdType !== undefined && insured.secondaryIdType !== 0
        ? insured.secondaryIdType
        : undefined,
    secondaryIdentityNumber: insured.secondaryIdNumber || undefined,
    secondaryIdNoExpiryDate: insured.secondaryIdNoExpiryDate || undefined,
    nationality: insured.nationality ?? 0,
    dualNationality: insured.dualNationality,
    alternateIdType:
      insured.alternateIdType !== undefined && insured.alternateIdType !== 0
        ? insured.alternateIdType
        : undefined,
    alternateIdNo: insured.alternateIdNo || undefined,
    alternateIdNoExpiryDate: insured.alternateIdNoExpiryDate || undefined,
    birthDate: insured.dateOfBirth,
    gender: insured.gender,
    ethnicity: insured.ethnicity ?? 0,
    address: populateAddress(insured.address ?? propertyData),
  }));
};

const getAddOn = (data: string[] | undefined) => {
  if (data !== undefined) {
    const newArray: { addOnCode: string }[] = [];
    data.forEach((element) => {
      newArray.push({
        addOnCode: element,
      });
    });
    return newArray;
  }
  return [];
};

export const getProposer = (
  data: PersonalDetail,
  bankData: BankAccountDetail,
  mailingData: AddressDetail,
): ProposerData => {
  return {
    type: 1,
    salutation: '',
    name: data.name,
    formerName: data.formerName,
    aliasName: data.aliasName,
    lastName: '',
    identityType: data.idType,
    identityNumber: data.idNumber.toString(),
    secondaryIdentityType:
      data.secondaryIdType !== undefined && data.secondaryIdType !== 0
        ? data.secondaryIdType
        : undefined,
    secondaryIdentityNumber: data.secondaryIdNumber || undefined,
    secondaryIdNoExpiryDate: data.secondaryIdNoExpiryDate || undefined,
    idNoExpiryDate: data.idNoExpiryDate || undefined,
    alternateIdType:
      data.alternateIdType !== undefined && data.alternateIdType !== 0
        ? data.alternateIdType
        : undefined,
    alternateIdNo: data.alternateIdNo,
    alternateIdNoExpiryDate: data.alternateIdNoExpiryDate || undefined,
    nationality: data.nationality ?? 0,
    dualNationality: data.dualNationality,
    email: data.email,
    mobile: data.mobileNo.includes('*') ? '' : data.mobileNo,
    home: '',
    office: '',
    birthDate: data.dateOfBirth,
    gender: data.gender,
    ethnicity: data.ethnicity ?? 0,
    address: populateAddress(mailingData),
    bankId: bankData.bankId,
    accountHolder: bankData.accountHolder,
    accountNo: bankData.accountNo,
  };
};

const populateAddress = (data: AddressDetail) => {
  return {
    addressLine1: data.addressLine1?.includes('*') ? '' : data.addressLine1,
    addressLine2: data.addressLine2?.includes('*') ? '' : data.addressLine2,
    city: data.city?.includes('*') ? '' : data.city,
    postcode: data.postcode?.includes('*') ? '' : data.postcode,
    state: data.state?.includes('*') ? '' : data.state,
    country: data.country?.includes('*') ? '' : data.country,
  };
};
